import {
  ApiError,
  NO_CONNECTION_STATUS_CODE,
  NOT_AUTHENTICATED_STATUS_CODES,
  Områdekode,
} from "~/src/api/types";
import { jaktdataApi } from "~/src/appGlobals";
import { isDeletedOnServer, syncStatusForError } from "~/src/contexts/AppDataContext/syncHelpers";
import { AppStorage, StorageItem } from "~/src/contexts/StorageContext/StorageProvider";
import { toErrorRecords } from "~/src/helpers/formErrors";

export const syncJaktomrader = async (storage: AppStorage) => {
  const pendingJaktomrader = storage
    .getAll<Områdekode>("PendingJaktomrade")
    .filter((e) => e.syncStatus != "unconfirmed");
  if (pendingJaktomrader.length === 0) return;

  const omradekodeForOmrade = (omrade: StorageItem<Områdekode>) => {
    return { Områdekode: omrade.data.Områdekode, Områdetype: omrade.data.Områdetype };
  };

  const toCreate = pendingJaktomrader.filter((jaktomrade) => jaktomrade.operation === "create");
  const toDelete = pendingJaktomrader.filter((jaktomrade) => jaktomrade.operation === "delete");

  let omraderSynced = 0;
  let omraderNotSynced = 0;

  let createRequests: Promise<void>[] = [];
  let deleteRequests: Promise<void>[] = [];

  const syncConfirm = (omrade: StorageItem<Områdekode>) => {
    omraderSynced += 1;
    if (omrade.operation === "delete") {
      storage.delete(omrade.objectKey, "PendingJaktomrade");
    } else {
      storage.add({
        ...omrade,
        syncStatus: "unconfirmed",
        isPending: false,
        errors: undefined,
      });
    }
  };

  const syncReject = (omrade: StorageItem<Områdekode>, e: ApiError) => {
    if (!NOT_AUTHENTICATED_STATUS_CODES.includes(e.statusCode)) {
      if (e.statusCode === NO_CONNECTION_STATUS_CODE) {
        //timeout error
        storage.add({
          ...omrade,
          syncStatus: "pending",
          errors: undefined,
        });
      } else {
        omraderNotSynced += 1;
        storage.add({
          ...omrade,
          syncStatus: syncStatusForError(e),
          errors: toErrorRecords(e),
        });
      }
    }
  };

  for (const omrade of toCreate) {
    createRequests.push(
      jaktdataApi
        .postJaktområde(omradekodeForOmrade(omrade))
        .then(() => syncConfirm(omrade))
        .catch((e: ApiError) => syncReject(omrade, e)),
    );
  }

  for (const omrade of toDelete) {
    deleteRequests.push(
      jaktdataApi
        .deleteJaktområde(omradekodeForOmrade(omrade))
        .then(() => syncConfirm(omrade))
        .catch((e: ApiError) => {
          if (isDeletedOnServer(e)) {
            syncConfirm(omrade);
          } else {
            syncReject(omrade, e);
          }
        }),
    );
  }

  return Promise.allSettled(createRequests)
    .then(() => {
      return Promise.allSettled(deleteRequests);
    })
    .then(() => {
      return { omraderSynced, omraderNotSynced };
    });
};

export type SyncJaktomraderResult = {
  omraderSynced: number;
  omraderNotSynced: number;
};

export const SyncJaktomraderResultDefaults: SyncJaktomraderResult = {
  omraderSynced: 0,
  omraderNotSynced: 0,
};
